import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./ContentAwaitingApproval.css";
import "../../Components/Header/Header.css";
import Header from "./../../Components/Header/Header";
import SuccessIcon from "../../assets/images/SuccessIcon.png";
import AwaitingApprovalIcon from "../../assets/images/AwaitingApprovalIcon.png";
import shapeLeft from "../../assets/images/shapeLeft.png";
import shapeRight from "../../assets/images/shapeRight.png";
import { callApi } from "../../utils/apiService";
import { videoCreatorEndPoint } from "../../Constants/constants";
import {
  formatDate,
  formatDuration,
  formatDurationSeconds,
  formatNotificationDate,
} from "../../utils/helpers";
import PaginationLeft from "../../assets/images/pagination-left-arrow.svg";
import PaginationRight from "../../assets/images/pagination-right-arrow.svg";
import Scheduled_Content_Search from "./../../assets/images/Scheduled_Content_Search.svg";
import RejectedIcon from "../../assets/images/RejectedIcon.png";
import { showToast } from "../../utils/toastUtils";

export default function ContentAwaitingApproval() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedContent, setSelectedContent] = useState(null);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showWaitingModal, setShowWaitingModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showScheduleDateModal, setShowScheduleDateModal] = useState(false);
  const [showScheduleTimeModal, setShowScheduleTimeModal] = useState(false);
  const [showScheduleSuccessModal, setShowScheduleSuccessModal] =
    useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonthNumber, setSelectedMonthNumber] = useState(
    new Date().getMonth(),
  );
  const [selectedPeriod, setSelectedPeriod] = useState("AM");
  const [timeSelectionStep, setTimeSelectionStep] = useState("hour"); // hour | minute
  const [selectedHours, setSelectedHours] = useState(12);
  const [selectedMinutes, setSelectedMinutes] = useState(0);
  const [timeError, setTimeError] = useState("");
  const [contentData, setContentData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [showRejectModal, setShowRejectModal] = useState(false);

  const itemsPerPage = 10;

  const getContentList = async (page = 1) => {
    try {
      const response = await callApi(
        `${videoCreatorEndPoint}contentDashboard/awaitingApproval?page=${page}&limit=${itemsPerPage}`,
        "GET",
      );

      if (response?.status) {
        setContentData(response.data?.content || []);
        setTotalPages(response.data?.totalPages || 0);
        setTotalItems(response.data?.totalItems || 0);
        setCurrentPage(response.data?.page || 1);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getContentList(currentPage);
  }, [currentPage]);

  const filteredContent = contentData?.filter((item) =>
    item?.title?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handlePostClick = (item) => {
    setSelectedContent(item);
    if (item.adminApprovalStatus === "PENDING") setShowWaitingModal(true);
    else if (item.adminApprovalStatus === "REJECTED") setShowRejectModal(true);
    else setShowPublishModal(true);
  };

  const handleCloseRejectModal = () => {
    setShowRejectModal(false);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handleScheduleLater = () => {
    setShowPublishModal(false);
    setShowScheduleDateModal(true);
  };

  const startIndex =
    totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;

  const endIndex = Math.min(currentPage * itemsPerPage, totalItems);

  const handlePublishNow = async () => {
    try {
      const response = await callApi(
        `${videoCreatorEndPoint}${selectedContent?.contentType === "Reel" ? "reel" : selectedContent?.contentType === "Story" ? "story" : "video"}/${selectedContent?.id}/publish`,
        "PATCH",
      );

      if (response?.status) {
        setShowPublishModal(false);
        setShowSuccessModal(true);

        getContentList(currentPage);
      } else {
        showToast("error", response?.alert || "Failed to publish content");
      }
    } catch (error) {
      setShowPublishModal(false);

      showToast(
        "error",
        error?.message || "Something went wrong while publishing content",
      );
    }
  };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  const isDateInPast = (year, month, day) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDateObj = new Date(year, month, day);
    selectedDateObj.setHours(0, 0, 0, 0);
    return selectedDateObj < today;
  };

  const handleDateSelect = (day) => {
    if (isDateInPast(selectedYear, selectedMonthNumber, day)) return;
    const newDate = new Date(selectedYear, selectedMonthNumber, day);
    setSelectedDate(newDate);
  };

  const handleHourSelect = (hour) => {
    setSelectedHours(hour);
    setTimeSelectionStep("minute");
    setTimeError("");
  };
  const handleMinuteSelect = (minute) => {
    setSelectedMinutes(minute);
    setTimeError("");
  };
  const hourValues = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

  const minuteValues = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  const clockValues = timeSelectionStep === "hour" ? hourValues : minuteValues;

  const handleMinuteClockClick = (e) => {
    if (timeSelectionStep !== "minute") return;

    const rect = e.currentTarget.getBoundingClientRect();

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const x = e.clientX - rect.left - centerX;
    const y = e.clientY - rect.top - centerY;

    let angle = (Math.atan2(y, x) * 180) / Math.PI + 90;

    if (angle < 0) {
      angle += 360;
    }

    let minute = Math.round(angle / 6);

    if (minute === 60) {
      minute = 0;
    }

    setSelectedMinutes(minute);
  };

  const updateTimeInput = (hour, minute) => {
    const h = String(hour).padStart(2, "0");
    const m = String(minute).padStart(2, "0");
  };

  const handleScheduleConfirm = async () => {
    if (selectedHours === null || selectedMinutes === null || !selectedPeriod) {
      setTimeError("Please select hour, minutes and AM/PM");
      return;
    }

    try {
      setTimeError("");

      const scheduledDate = new Date(selectedDate);
      let hours = selectedHours;
      if (selectedPeriod === "PM" && hours !== 12) {
        hours += 12;
      } else if (selectedPeriod === "AM" && hours === 12) {
        hours = 0;
      }

      scheduledDate.setHours(hours);
      scheduledDate.setMinutes(selectedMinutes);
      scheduledDate.setSeconds(0);
      scheduledDate.setMilliseconds(0);

      const response = await callApi(
        `${videoCreatorEndPoint}${selectedContent?.contentType === "Reel" ? "reel" : selectedContent?.contentType === "Story" ? "story" : "video"}/${selectedContent?.id}/schedule`,
        "PATCH",
        {
          scheduledAt: scheduledDate.toISOString(),
        },
      );

      if (response?.status) {
        setShowScheduleTimeModal(false);
        setShowScheduleSuccessModal(true);
      } else {
        showToast("error", response?.alert || "Failed to schedule post");
      }
    } catch (error) {
      showToast(
        "error",
        error?.message || "Something went wrong while scheduling the post",
      );
    }
  };

  const formatDateWithHour = (date) => {
    if (!date) return "";
    const options = { weekday: "short", month: "short", day: "numeric" };
    return date.toLocaleDateString("en-US", options);
  };

  const getScheduledDateTime = () => {
    const hours = String(selectedHours ?? 12).padStart(2, "0");
    const minutes = String(selectedMinutes ?? 0).padStart(2, "0");

    return `${formatDateWithHour(selectedDate)} at ${hours}:${minutes} ${selectedPeriod}`;
  };

  const getPostType = (item) => {
    const contentType = item?.contentType;
    if (contentType === "Video") {
      return "Post Video";
    } else if (contentType === "Reel") {
      return "Post Reel";
    } else {
      return "Post Story";
    }
  };

  const getMonthName = (month) => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[month];
  };

  const getStatusClass = (status) => {
    return status === "PENDING"
      ? "caa-status-pending"
      : status === "REJECTED"
        ? "caa-status-rejected"
        : "caa-status-verified";
  };
  const getBtnStatusClass = (status) => {
    return status === "PENDING"
      ? "caa-btn-status-pending"
      : status === "REJECTED"
        ? "caa-btn-status-pending"
        : "caa-btn-status-verified";
  };
  const getSelectedDateInfo = (date) => {
    if (!date) return null;

    return {
      year: date.getFullYear(),
      display: date.toLocaleDateString("en-US", {
        weekday: "short",
        day: "numeric",
        month: "short",
      }),
    };
  };
  const handlePreviousMonth = () => {
    if (selectedMonthNumber === 0) {
      setSelectedMonthNumber(11);
      setSelectedYear((prev) => prev - 1);
    } else {
      setSelectedMonthNumber((prev) => prev - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonthNumber === 11) {
      setSelectedMonthNumber(0);
      setSelectedYear((prev) => prev + 1);
    } else {
      setSelectedMonthNumber((prev) => prev + 1);
    }
  };

  return (
    <div className="dashboard">
      <div className="dashboard-bg-stripe"></div>
      <div
        className={`caa-page-wrapper ${
          showScheduleDateModal ||
          showScheduleTimeModal ||
          showPublishModal ||
          showWaitingModal ||
          showSuccessModal ||
          showScheduleSuccessModal
            ? "caa-page-wrapper-blur"
            : ""
        }`}
      >
        <Header />
        <div className="caa-container">
          <div className="caa-header">
            <div className="caa-content-wrapper">
              <h1 className="caa-title">Content Awaiting Approval</h1>
              <p className="caa-subtitle">
                Videos uploaded by users that are awaiting admin approval. These
                videos will be published only after verification.
              </p>

              {/* Search Bar */}
              <div className="caa-search-container">
                <div className="sc-search-wrapper">
                  <img
                    src={Scheduled_Content_Search}
                    alt="Search"
                    className="sc-search-icon"
                  />
                  <input
                    type="text"
                    className="sc-search-input"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="caa-wrapper">
            {/* Table Section */}
            <div className="caa-table-wrapper">
              <table className="caa-table">
                <thead>
                  <tr>
                    <th>Content</th>
                    <th>Type</th>
                    <th>Duration</th>
                    <th>Uploaded Date</th>
                    <th>Verified Status</th>
                    <th>Post</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContent.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <div className="caa-cell">
                          <img
                            src={
                              item?.contentType === "Video"
                                ? item.thumbnailImage
                                : item?.contentType === "Story"
                                  ? item?.thumbnailImage === ""
                                    ? item?.storyUrl
                                    : item?.thumbnailImage
                                  : item?.reelUrl
                            }
                            alt={item.title}
                            className="caa-thumbnail"
                          />
                          <span className="caa-name">{item.title}</span>
                        </div>
                      </td>
                      <td>{item.contentType}</td>
                      <td>{`${item?.contentType === "Video" ? formatDurationSeconds(item.videoDuration) : item?.contentType === "Story" ? formatDurationSeconds(item?.storyDuration) : formatDurationSeconds(item?.reelDuration)}`}</td>
                      <td>{formatDate(item.createdAt)}</td>
                      <td>
                        <span
                          className={`caa-status-badge ${getStatusClass(item.adminApprovalStatus)}`}
                        >
                          {item.adminApprovalStatus === "PENDING"
                            ? "Pending"
                            : item.adminApprovalStatus === "REJECTED"
                              ? "Admin Rejected"
                              : "Admin Verified"}
                        </span>
                      </td>
                      <td>
                        <button
                          className={`caa-post-button ${getBtnStatusClass(item.adminApprovalStatus)}`}
                          onClick={() => handlePostClick(item)}
                        >
                          {getPostType(item)}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="caa-pagination-container">
              <span className="caa-pagination-text">
                Showing {startIndex}-{endIndex} from {totalItems} data
              </span>
              <div className="pagination-controls">
                <img
                  src={PaginationLeft}
                  alt="Left Arrow"
                  style={{
                    width: "32px",
                    height: "32px",
                    cursor: currentPage > 1 ? "pointer" : "not-allowed",
                    opacity: currentPage > 1 ? 1 : 0.5,
                  }}
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                />

                {Array.from({ length: totalPages }, (_, index) => (
                  <button
                    key={index + 1}
                    className={`caa-pagination-page-number ${
                      currentPage === index + 1 ? "active" : ""
                    }`}
                    onClick={() => setCurrentPage(index + 1)}
                  >
                    {index + 1}
                  </button>
                ))}

                <img
                  src={PaginationRight}
                  alt="Right Arrow"
                  style={{
                    width: "32px",
                    height: "32px",
                    cursor:
                      currentPage < totalPages ? "pointer" : "not-allowed",
                    opacity: currentPage < totalPages ? 1 : 0.5,
                  }}
                  disabled={currentPage === totalPages || totalPages === 0}
                  onClick={handleNextPage}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Publish Confirmation Modal */}
      {showPublishModal && selectedContent && (
        <div className="caa-modal-overlay">
          <div className="caa-modal-content caa-publish-modal">
            <img src={SuccessIcon} alt="success" className="caa-success-icon" />
            <h2 className="caa-modal-title">
              Do You Want to Publish This Post Now?
            </h2>
            <p className="caa-modal-message">
              You can publish it now or schedule it for later.
            </p>
            <div className="caa-modal-buttons">
              <button
                className="caa-modal-button caa-schedule-button"
                onClick={handleScheduleLater}
              >
                Schedule for Later
              </button>
              <button
                className="caa-modal-button caa-publish-button"
                onClick={handlePublishNow}
              >
                Publish Now
              </button>
            </div>
          </div>
        </div>
      )}
      {showWaitingModal && selectedContent && (
        <div className="caa-modal-overlay">
          <div className="caa-modal-content caa-publish-modal">
            <img
              src={AwaitingApprovalIcon}
              alt="awaiting-approval"
              className="caa-success-icon"
            />
            <h2 className="caa-modal-title">Awaiting Approval</h2>
            <p className="caa-modal-message">
              You can publish this post after it has been approved by the admin.
            </p>
            <button
              className="caa-modal-button caa-okay-button"
              onClick={() => setShowWaitingModal(false)}
            >
              Okay
            </button>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && selectedContent && (
        <div className="caa-modal-overlay">
          <div className="caa-modal-content caa-success-modal">
            <img src={SuccessIcon} alt="success" className="caa-success-icon" />
            <h2 className="caa-modal-title">
              {selectedContent?.contentType} Published Successfully
            </h2>
            <p className="caa-modal-message">
              Your {selectedContent?.contentType?.toLowerCase()} has been
              published successfully.
            </p>
            <button
              className="caa-modal-button caa-okay-button"
              onClick={() => setShowSuccessModal(false)}
            >
              Okay
            </button>
          </div>
        </div>
      )}

      {/* Schedule Date Modal */}
      {showScheduleDateModal && (
        <div className="caa-modal-overlay">
          <div className="caa-modal-content caa-schedule-date-modal">
            <h3 className="caa-date-modal-title">Schedule publish Date</h3>

            {/* Year Selector */}
            {/* <div className="caa-year-selector">
                <img
                  src={shapeLeft}
                  className="caa-year-nav-btn"
                  onClick={() => setSelectedYear(selectedYear - 1)}
                />
                <span className="caa-year-display">{selectedYear}</span>
                <img
                  src={shapeRight}
                  className="caa-year-nav-btn"
                  onClick={() => setSelectedYear(selectedYear + 1)}
                />
              </div> */}

            <div className="caa-selected-date-box">
              {selectedDate ? (
                <>
                  <span className="caa-selected-year">
                    {getSelectedDateInfo(selectedDate).year}
                  </span>

                  <span className="caa-selected-date-text">
                    {getSelectedDateInfo(selectedDate).display}
                  </span>
                </>
              ) : (
                <>
                  <span className="caa-selected-year">{selectedYear}</span>
                  <span className="caa-selected-date-text">Select Date</span>
                </>
              )}
            </div>

            {/* Month Navigation */}
            <div className="caa-month-nav">
              <img
                src={shapeLeft}
                className="caa-month-nav-btn"
                onClick={handlePreviousMonth}
              />
              <span className="caa-month-display">
                {getMonthName(selectedMonthNumber)} {selectedYear}
              </span>
              <img
                src={shapeRight}
                className="caa-month-nav-btn"
                onClick={handleNextMonth}
              />
            </div>

            {/* Calendar Grid */}
            <div className="caa-calendar-wrapper">
              <div className="caa-calendar-weekdays">
                <div className="caa-weekday">S</div>
                <div className="caa-weekday">M</div>
                <div className="caa-weekday">T</div>
                <div className="caa-weekday">W</div>
                <div className="caa-weekday">T</div>
                <div className="caa-weekday">F</div>
                <div className="caa-weekday">S</div>
              </div>

              <div className="caa-calendar-days">
                {Array.from({
                  length: getFirstDayOfMonth(selectedYear, selectedMonthNumber),
                }).map((_, idx) => (
                  <div
                    key={`empty-${idx}`}
                    className="caa-calendar-day-empty"
                  ></div>
                ))}
                {Array.from({
                  length: getDaysInMonth(selectedYear, selectedMonthNumber),
                }).map((_, idx) => {
                  const day = idx + 1;
                  const isPast = isDateInPast(
                    selectedYear,
                    selectedMonthNumber,
                    day,
                  );
                  const isSelected =
                    selectedDate &&
                    selectedDate.getDate() === day &&
                    selectedDate.getMonth() === selectedMonthNumber &&
                    selectedDate.getFullYear() === selectedYear;

                  return (
                    <button
                      key={day}
                      className={`caa-calendar-day ${
                        isPast ? "caa-calendar-day-disabled" : ""
                      } ${isSelected ? "caa-calendar-day-selected" : ""}`}
                      onClick={() => handleDateSelect(day)}
                      disabled={isPast}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
              {timeError && <div className="caa-time-error">{timeError}</div>}
            </div>

            <div className="caa-schedule-modal-buttons">
              <button
                className="caa-schedule-modal-button caa-schedule-cancel-btn"
                onClick={() => setShowScheduleDateModal(false)}
              >
                Cancel
              </button>
              <button
                className="caa-schedule-modal-button caa-schedule-okay-btn"
                onClick={() => {
                  if (selectedDate) {
                    setShowScheduleDateModal(false);
                    setShowScheduleTimeModal(true);
                    setTimeSelectionStep("hour");
                  }
                }}
              >
                Okay
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Time Modal */}
      {showScheduleTimeModal && (
        <div className="caa-modal-overlay">
          <div className="caa-modal-content caa-schedule-time-modal">
            <h3 className="caa-time-modal-title">Schedule publish Time</h3>

            {/* Time Input Header */}
            <div className="caa-schedule-time-header">
              <div className="caa-time-display">
                <span
                  className={`caa-time-part ${
                    timeSelectionStep === "hour" ? "caa-time-part-active" : ""
                  }`}
                  onClick={() => setTimeSelectionStep("hour")}
                >
                  {String(selectedHours ?? 12).padStart(2, "0")}
                </span>
                :
                <span
                  className={`caa-time-part ${
                    timeSelectionStep === "minute" ? "caa-time-part-active" : ""
                  }`}
                  onClick={() => {
                    if (selectedHours !== null) {
                      setTimeSelectionStep("minute");
                    }
                  }}
                >
                  {String(selectedMinutes ?? 0).padStart(2, "0")}
                </span>
              </div>
              <div className="caa-schedule-ampm">
                <button
                  type="button"
                  className={`caa-ampm-btn ${
                    selectedPeriod === "AM" ? "caa-ampm-active" : ""
                  }`}
                  onClick={() => setSelectedPeriod("AM")}
                >
                  AM
                </button>

                <button
                  type="button"
                  className={`caa-ampm-btn ${
                    selectedPeriod === "PM" ? "caa-ampm-active" : ""
                  }`}
                  onClick={() => setSelectedPeriod("PM")}
                >
                  PM
                </button>
              </div>
            </div>
            <div className="caa-clock-face" onClick={handleMinuteClockClick}>
              {clockValues.map((value, index) => {
                const angle = ((index * 30 - 90) * Math.PI) / 180;
                const radius = timeSelectionStep === "hour" ? 130 : 130;

                const x = Math.cos(angle) * radius;
                const y = Math.sin(angle) * radius;

                const isSelected =
                  timeSelectionStep === "hour"
                    ? selectedHours === value
                    : Math.round(selectedMinutes / 5) * 5 === value;

                return (
                  <button
                    key={value}
                    className={`caa-clock-number ${
                      isSelected ? "caa-clock-number-selected" : ""
                    }`}
                    style={{
                      transform: `translate(${x}px, ${y}px)`,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();

                      if (timeSelectionStep === "hour") {
                        handleHourSelect(value);
                      } else {
                        handleMinuteSelect(value);
                      }
                    }}
                  >
                    {String(value).padStart(2, "0")}
                  </button>
                );
              })}
            </div>

            <div className="caa-schedule-modal-buttons">
              <button
                className="caa-schedule-modal-button caa-schedule-cancel-btn"
                onClick={() => {
                  setSelectedHours(12);
                  setSelectedMinutes(0);
                  setSelectedPeriod("AM");
                  setTimeSelectionStep("hour");
                  setTimeError("");

                  setShowScheduleTimeModal(false);
                }}
              >
                Cancel
              </button>
              <button
                className="caa-schedule-modal-button caa-schedule-okay-btn"
                onClick={handleScheduleConfirm}
              >
                Okay
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Success Modal */}
      {showScheduleSuccessModal && (
        <div className="caa-modal-overlay">
          <div className="caa-modal-content caa-schedule-success-modal">
            <img src={SuccessIcon} alt="success" className="caa-success-icon" />
            <h2 className="caa-published-title">Post Scheduled Successfully</h2>
            <p className="caa-published-message">
              The post has been successfully scheduled and will be published on{" "}
              {getScheduledDateTime()}.
            </p>
            <div className="caa-schedule-success-buttons">
              <button
                className="caa-schedule-modal-button caa-schedule-view-btn"
                onClick={() => {
                  setShowScheduleSuccessModal(false);
                  setSelectedDate(null);
                  setSelectedHours(12);
                  setSelectedMinutes(0);
                }}
              >
                Okay
              </button>
              <button
                className="caa-schedule-modal-button caa-schedule-schedule-more-btn"
                onClick={() => {
                  setShowScheduleSuccessModal(false);
                  navigate("/scheduled-content");
                }}
              >
                View Scheduled Posts
              </button>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && (
        <div className="modal-overlay" onClick={handleCloseRejectModal}>
          <div className="reject-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <img src={RejectedIcon} alt="rejected" />
              <h3>Content Not Approved</h3>
            </div>
            <p>
              Your content was not approved by the admin. Please review the
              reason below, make the required changes, and resubmit the video
              for approval.
            </p>
            <div className="reject-reason">
              <strong>Rejection Reason:</strong> Promotional content is not
              allowed.
            </div>
            <div className="modal-buttons">
              <button
                className="modal-btn keep-btn"
                onClick={handleCloseRejectModal}
              >
                Okay
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
